import React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@/app/_module/lib/utils';
import { controlBorder, displayFont, focusRing } from './primitives';

/** Shared class for text inputs, selects and textareas in the wizard. */
export const controlClass = cn('bg-white', controlBorder, focusRing);

/** Headline and lead for a wizard step. The one serif headline on the screen. */
export function StepIntro({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <header>
      <h2
        className="text-balance !text-[1.625rem] font-medium leading-[1.15] tracking-[-0.015em] text-neutral-900 sm:!text-[2rem]"
        style={displayFont}
      >
        {title}
      </h2>
      {children && (
        <div className="mt-3 max-w-[62ch] space-y-2 text-pretty text-[15px] leading-relaxed text-neutral-600 [&_strong]:font-medium [&_strong]:text-neutral-900">
          {children}
        </div>
      )}
    </header>
  );
}

/** Label, control, hint and error, stacked the same way for every field. */
export function Field({
  id,
  label,
  hint,
  error,
  className,
  children,
}: {
  id: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('min-w-0 space-y-1.5', className)}>
      <label htmlFor={id} className="block text-sm font-medium text-neutral-900">
        {label}
      </label>
      {children}
      {hint && <p className="text-pretty text-xs leading-relaxed text-neutral-500">{hint}</p>}
      {error && <p className="text-sm text-red-700">{error}</p>}
    </div>
  );
}

/** A value the user can see but not change, shown as text rather than a disabled input. */
export function ReadOnlyField({ label, value, hint }: { label: string; value: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <p className="text-sm font-medium text-neutral-900">{label}</p>
      <p className="break-words rounded-md bg-neutral-50 px-3 py-2.5 text-sm text-neutral-700 ring-1 ring-inset ring-neutral-200">
        {value}
      </p>
      {hint && <p className="text-pretty text-xs leading-relaxed text-neutral-500">{hint}</p>}
    </div>
  );
}

export const ChoiceGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root ref={ref} className={cn('grid gap-3', className)} {...props} />
));
ChoiceGroup.displayName = 'ChoiceGroup';

/**
 * A whole card as one radio option. Radix gives it roving focus and arrow keys;
 * the filled dot, not just the border colour, shows which one is chosen.
 */
export function Choice({
  value,
  title,
  description,
  children,
  className,
}: {
  value: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <RadioGroupPrimitive.Item
      value={value}
      className={cn(
        'group flex h-full w-full gap-3 rounded-xl border border-neutral-200 bg-white p-4 text-left transition-colors hover:border-neutral-400 data-[state=checked]:border-primary data-[state=checked]:bg-primary/[0.03] sm:p-5',
        focusRing,
      )}
    >
      <span
        aria-hidden
        className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-neutral-500 bg-white group-data-[state=checked]:border-primary"
      >
        <span className="size-2 rounded-full bg-primary opacity-0 transition-opacity group-data-[state=checked]:opacity-100" />
      </span>
      <span className={cn('block min-w-0 flex-1', className)}>
        <span className="block text-sm font-medium text-neutral-900">{title}</span>
        {description && <span className="mt-0.5 block text-pretty text-sm leading-relaxed text-neutral-500">{description}</span>}
        {children}
      </span>
    </RadioGroupPrimitive.Item>
  );
}
